import React, { useEffect, useState } from "react";

const roles = [
  "AI SOLUTIONS ARCHITECT",
  "AGENTIC AI BUILDER",
  "INSURANCE PROFESSIONAL",
  "BUILDER OF THINGS",
];

export default function Hero() {
  const [roleIdx, setRoleIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setRoleIdx((i) => (i + 1) % roles.length), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        overflow: "hidden",
      }}
    >
      {/* Orbital system — top right */}
      <div
        aria-hidden
        className="hero-orbit"
        style={{
          position: "absolute",
          top: "10%",
          right: "-6%",
          width: "min(48vw, 620px)",
          height: "min(48vw, 620px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        {/* concentric rings */}
        {[1, 0.72, 0.44].map((scale, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              inset: `${(1 - scale) * 50}%`,
              border: "1px solid rgba(52,224,255,0.18)",
              borderRadius: "50%",
              animation: `${i % 2 ? "spinOrbitRev" : "spinOrbit"} ${40 + i * 22}s linear infinite`,
            }}
          >
            {/* a satellite dot riding each ring */}
            <span
              style={{
                position: "absolute",
                top: "-4px",
                left: "50%",
                width: "8px",
                height: "8px",
                marginLeft: "-4px",
                borderRadius: "50%",
                background: i === 1 ? "var(--amber)" : "var(--cyan)",
                boxShadow: `0 0 12px ${i === 1 ? "var(--amber)" : "var(--glow)"}`,
              }}
            />
          </div>
        ))}
        {/* core */}
        <div
          style={{
            position: "absolute",
            inset: "42%",
            borderRadius: "50%",
            background: "radial-gradient(circle, var(--cyan), transparent 70%)",
            opacity: 0.5,
            animation: "pulseGlow 4s ease-in-out infinite",
          }}
        />
      </div>

      <div
        style={{
          maxWidth: "var(--maxw)",
          margin: "0 auto",
          width: "100%",
          padding: "120px clamp(20px, 5vw, 48px) 0",
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Status line */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontFamily: "var(--mono)",
            fontSize: "0.78rem",
            letterSpacing: "0.18em",
            color: "var(--cyan)",
            marginBottom: "clamp(18px, 4vh, 36px)",
            opacity: 0,
            animation: "riseIn 0.9s var(--ease) 0.1s forwards",
          }}
        >
          <span
            style={{
              width: "9px",
              height: "9px",
              borderRadius: "50%",
              background: "var(--cyan)",
              boxShadow: "0 0 10px var(--glow)",
              animation: "pulseGlow 1.6s ease-in-out infinite",
            }}
          />
          SYSTEM ONLINE
          <span style={{ color: "var(--text-faint)" }}>{"// ESPOO, FI · 60.2°N 24.6°E"}</span>
        </div>

        <h1
          style={{
            fontFamily: "var(--display)",
            fontWeight: 700,
            fontSize: "clamp(3rem, 12vw, 11rem)",
            lineHeight: 0.9,
            letterSpacing: "-0.01em",
            margin: 0,
            textTransform: "uppercase",
          }}
        >
          <span
            className="glow-text"
            style={{
              display: "block",
              opacity: 0,
              animation: "riseIn 1s var(--ease) 0.2s forwards",
            }}
          >
            Juhani
          </span>
          <span
            style={{
              display: "block",
              color: "var(--cyan)",
              WebkitTextStroke: "1px var(--cyan)",
              opacity: 0,
              animation: "riseIn 1s var(--ease) 0.36s forwards, flicker 6s 1.6s infinite",
              textShadow: "0 0 28px var(--glow)",
            }}
          >
            Rajalahti
          </span>
        </h1>

        {/* Rotating role readout */}
        <div
          style={{
            marginTop: "clamp(22px, 5vh, 44px)",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            opacity: 0,
            animation: "riseIn 1s var(--ease) 0.52s forwards",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "var(--mono)",
              fontSize: "0.8rem",
              color: "var(--text-faint)",
              letterSpacing: "0.1em",
            }}
          >
            ROLE&nbsp;»
          </span>
          <span
            key={roleIdx}
            className="glow-text"
            style={{
              fontFamily: "var(--display)",
              fontWeight: 600,
              fontSize: "clamp(1.1rem, 3.2vw, 2rem)",
              color: "var(--amber)",
              textShadow: "0 0 16px rgba(255,179,71,0.5)",
              letterSpacing: "0.04em",
              animation: "riseIn 0.55s var(--ease) forwards",
            }}
          >
            {roles[roleIdx]}
          </span>
          <span
            style={{
              width: "10px",
              height: "1.4em",
              background: "var(--cyan)",
              animation: "blink 1s step-end infinite",
            }}
          />
        </div>

        <a
          href="#about"
          style={{
            marginTop: "clamp(30px, 6vh, 56px)",
            alignSelf: "flex-start",
            fontFamily: "var(--mono)",
            fontSize: "0.78rem",
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            textDecoration: "none",
            color: "var(--text)",
            border: "1px solid var(--line-strong)",
            padding: "12px 20px",
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            background: "rgba(52,224,255,0.05)",
            transition: "all 0.3s ease",
            opacity: 0,
            animation: "riseIn 1s var(--ease) 0.7s forwards",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(52,224,255,0.16)";
            e.currentTarget.style.boxShadow = "0 0 20px rgba(52,224,255,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(52,224,255,0.05)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          INITIATE SCROLL
          <span style={{ color: "var(--cyan)", animation: "riseIn 1.4s ease-in-out infinite alternate" }}>↓</span>
        </a>
      </div>

      {/* Telemetry marquee at the bottom */}
      <div
        style={{
          marginTop: "clamp(40px, 7vh, 80px)",
          borderTop: "1px solid var(--line)",
          borderBottom: "1px solid var(--line)",
          background: "rgba(8,11,20,0.6)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          overflow: "hidden",
          padding: "12px 0",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            whiteSpace: "nowrap",
            width: "max-content",
            animation: "marquee 30s linear infinite",
          }}
        >
          {[0, 1].map((dup) => (
            <span key={dup} style={{ display: "flex" }} aria-hidden={dup === 1}>
              {[
                "AGENTFORCE", "DATA 360", "AWS BEDROCK", "AZURE AI FOUNDRY",
                "INSURANCE", "AGENTIC WORKFLOWS", "REACT", "GENERATIVE AI",
              ].map((t) => (
                <span
                  key={t}
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: "0.82rem",
                    letterSpacing: "0.14em",
                    padding: "0 24px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "24px",
                    color: "var(--text-soft)",
                  }}
                >
                  {t}
                  <span style={{ color: "var(--cyan)" }}>◆</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
